<script lang="ts">
	import { createEventDispatcher } from 'svelte';

	import { format } from 'src/services/i18n';
	import { IconButton, NavigationItem, Text } from '@Shelly/shelly-design-system';

	import HomeAvatar from './HomeAvatar.svelte';

	import type { Home } from 'src/types/Home';
	import { getHomeDisplayName } from 'src/helpers/houses/homes_helpers';
	import { user_data } from 'src/stores/account/user';
	import { homes } from 'src/stores/account/home';

	export let home: Home;
	export let isActive = false;
	export let isHomeListOpen = false;

	const dispatch = createEventDispatcher<{
		select: Home;
		manageHome: Home;
		toggleHomeMenu: void;
	}>();

	$: chevronButtonClass = isHomeListOpen ? 'home-row-chevron-btn is-open' : 'home-row-chevron-btn';

	const handleChevronClick = () => {
		if ($homes.list.length <= 1 || $user_data.installer) {
			return;
		}

		dispatch('toggleHomeMenu');
	};

	const handleRowClick = () => {
		dispatch('select', home);
	};

	// Prefer "Shared by {owner email}" (from the `shared_by` API field) and fall
	// back to the generic "Shared Home" label when no owner email is available.
	$: sharedLabel = home?.sharedByEmail
		? $format('settings_shared_by_label', {
				default: 'Shared by {email}',
				values: { email: home.sharedByEmail },
			})
		: $format('label_shared_home', { default: 'Shared Home' });
</script>

{#if isActive}
	<NavigationItem
		class="home-row-active-no-hover"
		style="padding: 15px 12px;"
		type="homes-dropdown-menu"
		title={getHomeDisplayName(home?.name, $user_data.email)}
		on:click={handleChevronClick}
	>
		<HomeAvatar size="s" {home} slot="avatar" />

		<svelte:fragment slot="subtitle">
			{#if home?.isShared}
				<Text
					class="home-row-item-subtitle text-bold text-color-muted"
					styleType="label-small"
					noWrap>{sharedLabel}</Text
				>
			{/if}
		</svelte:fragment>
		<svelte:fragment slot="trailing">
			{#if $homes.list.length > 1 && !$user_data.installer}
				<IconButton
					class={chevronButtonClass}
					icon="fa-solid fa-chevron-down"
					variant="secondary"
					aria-label={$format('button_home_options', { default: 'Home options' })}
				/>
			{/if}
		</svelte:fragment>
	</NavigationItem>
{:else}
	<NavigationItem
		class="homes-row-item"
		type="homes-dropdown-menu"
		title={getHomeDisplayName(home?.name, $user_data.email)}
		on:click={handleRowClick}
	>
		<HomeAvatar {home} slot="avatar" />
		<svelte:fragment slot="subtitle">
			{#if home?.isShared}
				<Text
					class="home-row-item-subtitle text-bold text-color-muted"
					styleType="label-small"
					noWrap>{sharedLabel}</Text
				>
			{/if}
		</svelte:fragment>
	</NavigationItem>
{/if}

<style>
	.home-row-item-subtitle {
		text-overflow: ellipsis;
		overflow: hidden;
	}

	:global(.home-row-active-no-hover.navigation-item-homes-dropdown-menu:hover:not(.disabled)) {
		background: transparent !important;
		background-color: transparent !important;
	}

	:global(.home-row-chevron-btn.icon-button.secondary:not(.disabled-overlay, .active):active) {
		box-shadow: none !important;
	}

	@media (hover: hover) and (pointer: fine) {
		:global(.home-row-chevron-btn.icon-button.secondary:not(.disabled-overlay, .active):hover) {
			box-shadow: none !important;
		}
	}

	:global(.home-row-chevron-btn .icon-grid) {
		transition: transform 0.2s ease-in-out;
	}

	:global(.home-row-chevron-btn.is-open .icon-grid) {
		transform: rotate(180deg);
	}
</style>
